import { apiRequest } from '../lib/api-client';

/** Importing a spreadsheet (context.md sections 30, 134). */

export type ImportEntity = 'LEAD' | 'COMPANY' | 'CONTACT';
export type DuplicateStrategy = 'SKIP' | 'UPDATE' | 'CREATE';

export type Mapping = Record<string, string | null>;

export interface ImportPreview {
  headers: string[];
  rows: string[][];
  delimiter: string;
  mapping: Mapping;
  totalRows: number;
}

export function previewImport(input: {
  fileId: string;
  entityType: ImportEntity;
}): Promise<ImportPreview> {
  return apiRequest('/v1/imports/preview', { method: 'POST', body: input });
}

export interface ImportJob {
  id: string;
  entity_type: ImportEntity;
  status: 'PENDING' | 'RUNNING' | 'DONE' | 'FAILED' | 'CANCELED';
  duplicates: DuplicateStrategy;
  total_rows: number;
  imported: number;
  updated: number;
  skipped: number;
  failed: number;
  errors: { row: number; reason: string }[];
  failure_reason: string | null;
  created_at: string;
  finished_at: string | null;
}

export function startImport(input: {
  fileId: string;
  entityType: ImportEntity;
  mapping: Mapping;
  duplicates?: DuplicateStrategy;
  options?: { ownerId?: string | null; source?: string | null };
}): Promise<ImportJob> {
  return apiRequest('/v1/imports', { method: 'POST', body: input });
}

export function listImports(): Promise<{ imports: ImportJob[] }> {
  return apiRequest('/v1/imports?limit=20');
}

export function getImport(id: string): Promise<ImportJob> {
  return apiRequest(`/v1/imports/${id}`);
}

/** The fields each entity can be given, in the order a mapping screen should offer them. */
export const IMPORT_FIELDS: Record<ImportEntity, string[]> = {
  LEAD: ['name', 'email', 'phone', 'company_name', 'job_title', 'source'],
  COMPANY: ['name', 'legal_name', 'document', 'email', 'phone', 'website', 'industry'],
  CONTACT: ['first_name', 'last_name', 'email', 'phone', 'mobile', 'job_title'],
};

/** A job that is still moving, so the screen knows whether to keep asking. */
export function isRunning(job: ImportJob): boolean {
  return job.status === 'PENDING' || job.status === 'RUNNING';
}
